/**
 * ACCESS LAYER — persona-aware gating primitives.
 *
 * Built on top of PR #96's `useCurrentUser()` (the single persona/module
 * context) and the typed `FEATURE_CATALOG` (module id → surface). This file
 * does NOT own a parallel persona context or a second module seed — it reads
 * the canonical `canSeeModule` / `currentPersona` from `useCurrentUser()` and
 * the catalog's tab/route mapping.
 *
 * Module-visibility contract (useModuleEnabled / useVisibleModules):
 *   Both delegate the whole decision to `useCurrentUser().canSeeModule`, which
 *   is now SERVER-DRIVEN (ACT-1303) and owns the seam + error handling itself:
 *   - HARD ERROR (currentUser query errored/aborted) → fail CLOSED (every gated
 *     module hidden), so a backend outage doesn't expose gated surfaces.
 *   - no error + no persona payload (`raw.currentPersona == null`) → allow
 *     everything (the QA/dev transition seam), preserving the pre-RBAC
 *     experience until the backend enforces personas in all envs.
 *   - persona payload present → visible iff the module id appears in
 *     `raw.currentPersona.modules[]` (the backend's active-module list);
 *     omitted modules are hidden (fail-closed). There is intentionally NO
 *     admin-role all-access on the visibility path — an admin with an active
 *     persona sees exactly that persona's modules (consistent with
 *     useVisibleHomeCards). See CurrentUserContext's `canSeeModule`.
 *
 * Action / data-scope contract (useModuleActions / useActionAllowed / useDataScope):
 *   reads the RAW backend payload and makes a THREE-WAY decision keyed on
 *   `raw.currentPersona` — the deliberate distinction between "no persona payload
 *   at all" (transition seam → ALLOW) and "a present payload that omits this
 *   module" (deliberate non-grant → DENY / fail-CLOSED). See `resolveModuleActions`.
 */

import { useMemo } from "react";

// eslint-disable-next-line import/no-unresolved
import type { ModuleGroup, ModuleObject } from "@astrana/ui/modules";

import { useCurrentUser, type CurrentUser } from "../auth/CurrentUserContext";
import {
  cardModuleIdForType,
  catalogActions,
  isHomeCardModuleId,
  isModuleId,
  moduleIdForTabName,
  type ModuleId,
} from "../config/featureCatalog";
import { ROLES, isHiddenPersona } from "../config/roles";

// Re-export the gate component so call sites have one import surface.
export { FeatureGate } from "./FeatureGate";

/**
 * Is `moduleId` enabled for the active persona?
 *   - true when no persona is active (allow-all fallback), or
 *   - true when the active persona grants the module.
 *
 * Delegates the persona→module decision to `useCurrentUser().canSeeModule`,
 * the canonical gate (PR #96), so there is exactly one place that knows the
 * persona→module mapping. `isModuleId` validation lives at the catalog boundary
 * (`useVisibleModules`, FeatureGate's typed `module` prop) — by the time an id
 * reaches here it is already a known ModuleId.
 */
export function useModuleEnabled(moduleId: ModuleId): boolean {
  const { canSeeModule } = useCurrentUser();
  // `canSeeModule` now owns the transition seam (no persona payload → allow all)
  // and the fail-closed decision — no separate `currentPersona === null` branch.
  return canSeeModule(moduleId);
}

/**
 * Filter a MemberProfile `ModuleGroup[]` down to what the active persona may see.
 *
 * A tab is DROPPED only when ALL of:
 *   - a persona is active (currentPersona !== null),
 *   - its `tabName` maps (via the catalog) to a known controlling ModuleId, and
 *   - that module is NOT enabled for the persona.
 *
 * EVERY tab now maps (AC-ADM-3, 2026-08-21). Diagnoses, Labs and Scribe were the
 * last tabs without a FEATURE_CATALOG entry — they are registered now, so the
 * `!moduleId` branch below is dead for every group MemberProfilePage builds. It
 * is kept only as a defensive contract for an unrecognised `tabName`; do NOT
 * lean on it to ship a new "always visible" tab, because a tab that skips this
 * gate also skips `useTabOrder` (no ModuleId ⇒ no sortOrder ⇒ stranded at the
 * tail, unorderable from the admin console).
 *
 * This is the SINGLE tab-filter path, and MemberProfilePage now applies it AFTER
 * merging its flag-gated extras (Notes / Scribe / Documents), so those groups are
 * gated by exactly the same rule as the statically-declared ones — feature flag
 * decides whether the group is CONSTRUCTED, this gate decides whether the persona
 * SEES it. Flag AND persona, never flag alone.
 *
 * Fail-closed by construction: when a persona is active, a catalog-mapped tab is
 * kept only if `canSeeModule` grants it (the PERSONA_MODULES seed). A module
 * omitted from the active persona's grant set is therefore NOT visible — the same
 * "omitted = denied" principle the action layer applies to a present payload.
 *
 * IMPORTANT: pass a STABLE `base` array reference. `base` is a `useMemo`
 * dependency here, so a fresh array literal on every render would defeat the memo
 * and re-filter each render. Callers should memoize the `base` they pass in.
 */
export function useVisibleModules(base: ModuleGroup[]): ModuleGroup[] {
  const { canSeeModule } = useCurrentUser();
  return useMemo(() => {
    // `canSeeModule` owns the transition seam (no persona payload → allow all),
    // so every gated group is filtered through it uniformly — no separate
    // `currentPersona === null` short-circuit.
    return base.filter((group) => {
      const moduleId = moduleIdForTabName(group.tabName);
      // Dead branch for every real tab as of AC-ADM-3 — see the doc comment
      // above. Deliberately NOT changed to fail closed: an unrecognised tabName
      // is a catalog bug, and silently blanking a tab is harder to diagnose than
      // seeing an ungated one. Add the catalog entry, don't rely on this.
      if (!moduleId) return true; // unrecognised tabName — no catalog entry
      // `moduleId` is guaranteed a known ModuleId here (it came from the catalog
      // reverse lookup); the isModuleId call is purely for type narrowing.
      return isModuleId(moduleId) ? canSeeModule(moduleId) : true;
    });
  }, [base, canSeeModule]);
}

/**
 * Orders a MemberProfile `ModuleGroup[]` by the active persona's server-driven
 * per-module `sortOrder` (admin-settable tab order). Reuses `sortByPersonaOrder`
 * (below) via the same `tabName -> ModuleId` catalog reverse lookup
 * `useVisibleModules` uses — a group whose tabName has no controlling ModuleId,
 * or whose module is absent from `raw.currentPersona.modules[]`, resolves to an
 * undefined sortOrder and therefore keeps its ORIGINAL relative position
 * (stable, sorted after every group that does have a sortOrder).
 *
 * AC-ADM-3: the first of those two cases no longer happens — every MemberProfile
 * tab has a catalog entry now (Diagnoses/Labs/Scribe were the holdouts, and were
 * exactly why those three appeared pinned to the end of the tab strip no matter
 * what the admin console did). Only the second remains, and it is the intended
 * behaviour for a module the persona was granted with no explicit sort_order.
 *
 * FAIL-SAFE BY CONSTRUCTION: with no persona payload at all
 * (`raw?.currentPersona == null` — the transition seam), every group resolves
 * to an undefined sortOrder, so the result is `groups` in its original order,
 * unchanged — no separate "no config" branch is needed here.
 *
 * Caller MUST pass `groups` already visibility-filtered AND with any
 * flag-gated extras merged in (MemberProfilePage's `visibleModulesWithExtras`)
 * — sorting before extras are appended would leave them stranded, unordered,
 * at the tail.
 */
export function useTabOrder(groups: ModuleGroup[]): ModuleGroup[] {
  const { raw } = useCurrentUser();
  const personaModules = raw?.currentPersona?.modules;

  const sortOrderByModuleId = useMemo(
    () => new Map((personaModules ?? []).map((m) => [m.moduleId, m.sortOrder] as const)),
    [personaModules],
  );

  return useMemo(
    () =>
      sortByPersonaOrder(groups, (group) => {
        const moduleId = moduleIdForTabName(group.tabName);
        return moduleId ? sortOrderByModuleId.get(moduleId) : undefined;
      }),
    [groups, sortOrderByModuleId],
  );
}

// ---------------------------------------------------------------------------
// Home card filter/order (ACT-1303) — reads raw.currentPersona.modules[].
// ---------------------------------------------------------------------------

/**
 * Stable two-tier comparator used to order items by an optional per-item
 * `sortOrder`:
 *   1. Items WITH a defined sortOrder sort before items without one.
 *   2. Among items with a sortOrder, order ascending by that value.
 *   3. Ties (equal sortOrder, or both missing a sortOrder) keep their
 *      original relative position — magnitude-independent, no assumptions
 *      about the sortOrder value range so backend renumbering never breaks it.
 *
 * Generic over `T` so it isn't tied to the Home-card use case; `sortOrderOf`
 * supplies the (possibly undefined) order for an item.
 */
export function sortByPersonaOrder<T>(
  items: readonly T[],
  sortOrderOf: (item: T) => number | undefined,
): T[] {
  return items
    .map((item, originalIndex) => ({
      item,
      originalIndex,
      sortOrder: sortOrderOf(item),
    }))
    .sort((a, b) => {
      const aHasOrder = a.sortOrder !== undefined;
      const bHasOrder = b.sortOrder !== undefined;
      if (aHasOrder !== bHasOrder) return aHasOrder ? -1 : 1;
      if (aHasOrder && bHasOrder && a.sortOrder !== b.sortOrder) {
        return (a.sortOrder as number) - (b.sortOrder as number);
      }
      return a.originalIndex - b.originalIndex; // stable tie-break
    })
    .map(({ item }) => item);
}

/**
 * Filters and orders the Home tab's 6 module cards per the active persona's
 * server-driven config (`raw.currentPersona.modules[]`), independently of the
 * whole-tab gate in `useVisibleModules` (which only decides whether the Home
 * TAB itself is shown).
 *
 * Contract (kept CONSISTENT with the tab gate in canSeeModule — ACT-1303):
 *   - HARD ERROR (currentUser query errored/aborted — `error` non-null) → return
 *     `[]` (NO cards). Fail CLOSED on outage, exactly like canSeeModule.
 *   - no error + `raw.currentPersona === null` (no persona payload at all — the
 *     QA/dev transition seam) → return `cards` unchanged (allow-all seam).
 *   - persona payload present → keep ONLY the cards whose backend card id
 *     appears in `raw.currentPersona.modules[]`, ordered by `sortOrder` (see
 *     `sortByPersonaOrder`). A persona configured with ZERO Home cards therefore
 *     correctly shows ZERO cards — matching how the tab gate fails closed on an
 *     empty `modules[]`. (The old "no card ids in payload → show all" fail-OPEN
 *     branch was removed: it was inconsistent with tabs and let a persona that
 *     deliberately hides every Home card instead show all six.)
 *
 * `cards` must be a STABLE reference (see `useVisibleModules`'s note) — the
 * caller should hold the Home group's `modules` array in a module-level
 * constant or memo.
 */
export function useVisibleHomeCards(cards: ModuleObject[]): ModuleObject[] {
  const { raw, error, isAdmin, enforcement } = useCurrentUser();
  const personaModules = raw?.currentPersona?.modules;
  // "No REAL active persona" — null OR the vestigial ADMIN persona. Mirrors the
  // canSeeModule scoped admin bypass so the Home cards agree with the tab gate.
  const noRealPersona =
    raw?.currentPersona == null || isHiddenPersona(raw.currentPersona.code);

  // Narrow to only the Home-card entries so downstream memoization keys off
  // this small derived slice rather than the whole `raw` payload (which also
  // carries unrelated permissions/role data that changes independently).
  const cardEntries = useMemo(
    () => (personaModules ?? []).filter((m) => isHomeCardModuleId(m.moduleId)),
    [personaModules],
  );

  return useMemo(() => {
    // HARD ERROR → fail CLOSED: hide every card. Checked FIRST (an errored query
    // leaves raw null, which the seam below would otherwise read as allow-all).
    // Consistent with canSeeModule — a backend outage must not expose all cards.
    if (error != null) return [];

    // SCOPED ADMIN BYPASS (ACT-1319) → all cards when an admin has no REAL active
    // persona (null OR vestigial ADMIN). Mirrors canSeeModule; an admin previewing
    // a real persona has noRealPersona === false and falls through to the
    // per-persona filter below.
    if (isAdmin && noRealPersona) return cards;

    // No persona payload at all (and no error) → the seam split (ACT-1319):
    // enforcement OFF → allow-all transition seam (all cards); ON → fail CLOSED
    // (no cards for an unprovisioned non-admin). Keyed on the RAW backend payload,
    // consistent with canSeeModule.
    if (raw?.currentPersona == null) return enforcement ? [] : cards;

    // Persona payload present → show ONLY the Home cards present in the payload.
    // A persona configured with zero Home cards ends up with an empty
    // sortOrderByCardId here → `configured` is [] → ZERO cards render, matching
    // the tab gate's fail-closed-on-empty behavior (there is deliberately no
    // "empty → show all" fallback).
    const sortOrderByCardId = new Map(
      cardEntries.map((m) => [m.moduleId, m.sortOrder] as const),
    );

    const configured = cards.filter((card) => {
      const cardId = cardModuleIdForType(card.type);
      return cardId !== undefined && sortOrderByCardId.has(cardId);
    });

    return sortByPersonaOrder(configured, (card) => {
      const cardId = cardModuleIdForType(card.type);
      return cardId ? sortOrderByCardId.get(cardId) : undefined;
    });
  }, [cards, raw?.currentPersona, cardEntries, error, isAdmin, enforcement, noRealPersona]);
}

// ---------------------------------------------------------------------------
// Action gating — reads per-module actions from the raw persona payload.
// ---------------------------------------------------------------------------

/**
 * Pure resolver for the per-module action list. Shared by `useModuleActions`
 * and `useActionAllowed` so the access decision lives in exactly one place (and
 * `useCurrentUser()` is subscribed to once per consuming component).
 *
 * DECISION ORDER — the security-critical distinction is between "the backend
 * is not yet returning persona data at all" (transition/QA/offline seam) and
 * "the backend returned a real persona payload that OMITS this module" (a
 * deliberate non-grant, must DENY):
 *
 *   0. Admin ROLE → all-access (catalog). Checked first so enforcement never
 *      locks an admin out (ACT-1319).
 *
 *   1. `raw?.currentPersona == null` — NO persona payload at all. The seam split
 *      (ACT-1319): `enforcement` OFF → catalog fallback (ALLOW, the pre-server-
 *      enforcement seam); ON → `[]` (DENY, an unprovisioned non-admin gets no
 *      actions). Retire the OFF path only once enforcement has soaked ON in every
 *      env and the flag is removed.
 *
 *   2. Persona payload IS present, module entry NOT found — the module was
 *      omitted from a real grant list → FAIL CLOSED, return [] (DENY).
 *
 *   3. Persona payload present, entry found:
 *      - `entry.actions == null` → backend granted the module with no per-action
 *        restriction → ALLOW (catalog action registry).
 *      - `entry.actions` present → those are the only enabled actions.
 *
 * Note this keys on the RAW backend payload (`raw.currentPersona`), not the
 * FE-normalized `currentPersona` string (which dev overrides also set) — the
 * transition seam is specifically "backend returned no persona data".
 */
export function resolveModuleActions(
  raw: CurrentUser["raw"],
  moduleId: ModuleId,
  enforcement: boolean,
): string[] {
  // (0) Admin ROLE → all-access: grant the full catalog action set for every
  // module regardless of persona. Checked FIRST so an admin is never locked out
  // by the enforcement seam (ACT-1319) — an admin with no persona still gets the
  // catalog. Mirrors canSeeModule's admin handling; keyed on the ROLE (single
  // admin signal) — the old ADMIN super-persona was removed. (The actions layer's
  // admin all-access is intentionally UNSCOPED — unlike the visibility layer's
  // scoped bypass — preserving ACT-1303's existing behavior.)
  if (raw?.role?.code === ROLES.ADMIN) {
    return catalogActions(moduleId) as string[];
  }

  // (1) No persona payload at all → the seam split (ACT-1319): enforcement OFF →
  // pre-server-enforcement seam (ALLOW via catalog); ON → fail CLOSED ([]) for a
  // non-admin unprovisioned user (admins already returned above).
  if (raw?.currentPersona == null) {
    return enforcement ? [] : (catalogActions(moduleId) as string[]);
  }

  const entry = raw.currentPersona.modules.find((m) => m.moduleId === moduleId);

  // (2) Real payload omits this module → FAIL CLOSED (DENY).
  if (!entry) return [];

  // (3a) Granted, no per-action restriction → ALLOW (catalog registry).
  if (entry.actions == null) {
    return catalogActions(moduleId) as string[];
  }

  // (3b) Explicit action list → only those are enabled.
  return entry.actions;
}

/**
 * Returns the enabled action keys for `moduleId` under the active persona.
 * See `resolveModuleActions` for the three-way ALLOW/DENY decision.
 */
export function useModuleActions(moduleId: ModuleId): string[] {
  const { raw, error, enforcement } = useCurrentUser();
  // HARD ERROR → fail CLOSED: deny every action. Mirrors the visibility gate
  // (canSeeModule / useVisibleHomeCards) — a backend outage leaves raw null,
  // which resolveModuleActions would otherwise read as the allow-all seam and
  // grant the full catalog. Context `error` is null under a dev override /
  // skipped query, so `error != null` is specifically a hard query error.
  if (error != null) return [];
  return resolveModuleActions(raw, moduleId, enforcement);
}

/**
 * Returns true if `action` is enabled for `moduleId` under the active persona.
 *
 * Subscribes to `useCurrentUser()` ONCE and feeds the same `raw` payload to the
 * shared resolver — no second context subscription, no conditional hooks. The
 * resolver already encodes the transition seam (no persona payload → catalog
 * fallback) and the fail-closed path (present payload omitting the module → []),
 * so membership in the resolved list is the whole decision.
 */
export function useActionAllowed(moduleId: ModuleId, action: string): boolean {
  const { raw, error, enforcement } = useCurrentUser();
  // HARD ERROR → fail CLOSED: no action is allowed. Mirrors the visibility
  // gate's fail-closed-on-error (see useModuleActions).
  if (error != null) return false;
  return resolveModuleActions(raw, moduleId, enforcement).includes(action);
}

// ---------------------------------------------------------------------------
// Configured default (landing) tab — admin-settable via a "defaultTab" actions
// sentinel, mirroring the existing "collapsedByDefault" sentinel pattern
// (actions is unvalidated free-text JSONB; no backend/DDL change).
// ---------------------------------------------------------------------------

/**
 * Pure resolver: returns the `tabName` of the FIRST group in
 * `orderedVisibleGroups` (i.e. in the FINAL sorted, visibility-filtered tab
 * order) whose controlling module's actions include the `"defaultTab"`
 * sentinel, or `undefined` if none do.
 *
 * Reads `raw.currentPersona.modules[].actions` DIRECTLY — deliberately NOT
 * via `resolveModuleActions` — mirroring how `useTabOrder` reads `sortOrder`
 * straight off the same payload. `"defaultTab"` is config metadata, not a
 * permission: routing it through `resolveModuleActions` would hit that
 * resolver's ADMIN-role branch, which short-circuits to `catalogActions()`
 * (the registered PERMISSION vocabulary) and ignores the persona payload's
 * `actions` entirely — so an admin previewing a persona would see that
 * persona's tab ORDER honored but its configured default tab silently
 * ignored (the sentinel can never appear in `catalogActions()`). Reading the
 * payload directly here keeps the admin-preview experience consistent with
 * every other persona-driven surface (sortOrder, Home cards) and is why this
 * function does NOT take an `enforcement` param: there is no seam/permission
 * decision to make, only "does the payload carry the metadata or not".
 *
 * No persona payload at all (`raw?.currentPersona == null` — the transition
 * seam, or an errored query the caller already fails closed on) → `undefined`,
 * same as if no module carried the sentinel; the caller's Home/first-visible
 * fallback chain takes over.
 *
 * Because `orderedVisibleGroups` is already the tab list a user can actually
 * see, in its final display order:
 *   - a sentinel on a hidden/absent/inactive tab is never considered (it
 *     simply isn't in the array) → the caller's fallback chain takes over;
 *   - if more than one module carries the sentinel (the DB can't enforce
 *     exclusivity), the first match in display order wins — deterministic.
 */
export function resolveConfiguredDefaultTab(
  orderedVisibleGroups: readonly ModuleGroup[],
  raw: CurrentUser["raw"],
): string | undefined {
  const personaModules = raw?.currentPersona?.modules;
  if (!personaModules) return undefined;

  for (const group of orderedVisibleGroups) {
    const moduleId = moduleIdForTabName(group.tabName);
    if (!moduleId) continue;
    const entry = personaModules.find((m) => m.moduleId === moduleId);
    if (entry?.actions?.includes("defaultTab")) {
      return group.tabName;
    }
  }
  return undefined;
}

/**
 * Hook wrapper for `resolveConfiguredDefaultTab` — subscribes to
 * `useCurrentUser()` once and feeds its `raw` payload to the pure resolver.
 * HARD ERROR → fail closed (`undefined`, same as `useModuleActions`), so an
 * outage falls back to the caller's unconfigured-default chain rather than
 * risk landing on a stale/incorrect tab.
 */
export function useConfiguredDefaultTab(
  orderedVisibleGroups: ModuleGroup[],
): string | undefined {
  const { raw, error } = useCurrentUser();
  return useMemo(() => {
    if (error != null) return undefined;
    return resolveConfiguredDefaultTab(orderedVisibleGroups, raw);
  }, [orderedVisibleGroups, raw, error]);
}

/**
 * Returns the normalized data scope for `moduleId` from the active persona's
 * raw module payload. Null values from the backend are normalized to safe
 * defaults (`requireIpaMatch: false`, `allowedLineOfBusiness: []`).
 *
 * Returns `null` (no scope restriction) when:
 *   - `raw?.currentPersona == null` — no backend persona payload (transition seam),
 *   - the module entry is absent from a present payload, OR
 *   - the entry's `dataScope` is null.
 *
 * @phase2 / @todo: this hook only REPORTS the scope — NOTHING enforces it at the
 * data layer yet. Apollo queries are not filtered by `requireIpaMatch` /
 * `allowedLineOfBusiness`; wiring the scope into the data fetch (server-side row
 * filtering or client query variables) is Phase 2 work. Do not treat a non-null
 * return here as an access guarantee.
 */
export function useDataScope(
  moduleId: ModuleId,
): { requireIpaMatch: boolean; allowedLineOfBusiness: string[] } | null {
  const { raw } = useCurrentUser();

  // No backend persona payload → transition seam, no scope to apply.
  if (raw?.currentPersona == null) return null;

  const entry = raw.currentPersona.modules.find((m) => m.moduleId === moduleId);

  if (!entry || entry.dataScope === null) return null;

  return {
    requireIpaMatch: entry.dataScope.requireIpaMatch ?? false,
    allowedLineOfBusiness: entry.dataScope.allowedLineOfBusiness ?? [],
  };
}
